import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  api,
  can,
  type MeView,
  type ScheduleDecisionView,
  type SchedulePlan,
  type SchedulerConfig,
} from '../api.ts';
import { message, plural } from '../format.ts';
import './sweep.css';

/**
 * Run the sweep now, and set the deployment cadence.
 *
 * The Schedule screen is a preview and enqueues nothing — that is load-bearing,
 * because a preview mistaken for a trigger makes an operator afraid to open it.
 * This screen is the deliberate counterpart: the one place a sweep is started by
 * hand, and the one place the deployment-wide cadence is set. Both are owner
 * work (`deployment.manage`), because both act across the whole estate.
 *
 * **Running enqueues real scans**, and the copy says so plainly. The result is
 * rendered in the same shape the Schedule preview uses, so "what would happen"
 * and "what just happened" read identically — the difference is only that this
 * one acted.
 */

interface SweepProps {
  readonly me: MeView;
}

export const Sweep = ({ me }: SweepProps) => {
  const canManage = can(me, 'deployment.manage');

  return (
    <div className="sweep-screen">
      <p className="lead">
        DriftWatch re-assesses each connected client on a cadence. Here you can run that sweep now
        rather than waiting for the next tick, and set the deployment-wide defaults it follows.
      </p>

      <RunPanel canManage={canManage} />
      <ConfigPanel canManage={canManage} />
    </div>
  );
};

interface RunPanelProps {
  readonly canManage: boolean;
}

const RunPanel = ({ canManage }: RunPanelProps) => {
  const [plan, setPlan] = useState<SchedulePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous.endsWith('\u200B') ? text : `${text}\u200B`));
  }, []);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.runSweep();
      setPlan(result);
      announce(`Sweep run. ${plural(result.enqueue.length, 'client')} queued.`);
    } catch (cause) {
      setError(message(cause));
      announce('The sweep did not run.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card sweep-card" aria-labelledby="sweep-run-heading">
      <div className="card-head">
        <h2 id="sweep-run-heading">Run the sweep now</h2>
      </div>
      <div className="card-body">
        <p aria-live="polite" role="status" className="sr-only">
          {announcement}
        </p>

        <div className="callout callout-caution sweep-warn">
          <span className="callout-title">This starts scans</span>
          Unlike the Schedule preview, running the sweep enqueues a real assessment for every client
          that is due. It is safe to repeat — a manual run and the scheduled tick on the same day
          collapse into one scan rather than doubling it — but it is not a preview.
        </div>

        {error !== null && <div className="error sweep-error">{error}</div>}

        {canManage ? (
          <button
            type="button"
            className="btn btn-primary"
            aria-busy={busy || undefined}
            disabled={busy}
            onClick={() => void run()}
          >
            {busy ? 'Running…' : 'Run sweep now'}
          </button>
        ) : (
          <p className="note">Running the sweep needs the deployment.manage capability.</p>
        )}

        {plan !== null && <RunResult plan={plan} />}
      </div>
    </section>
  );
};

interface RunResultProps {
  readonly plan: SchedulePlan;
}

const RunResult = ({ plan }: RunResultProps) => (
  <div className="sweep-result">
    <p className="status-inline sweep-result-summary" role="status">
      {plan.enqueue.length === 0
        ? 'Nothing was due, so nothing was queued.'
        : `${plural(plan.enqueue.length, 'client')} queued on this sweep.`}
      {plan.deferred.length > 0 &&
        ` ${plan.deferred.length} more ${plan.deferred.length === 1 ? 'was' : 'were'} due but held for a following sweep.`}
    </p>

    <PlanGroup
      heading="Queued on this sweep"
      note="Each of these has had an assessment enqueued."
      rows={plan.enqueue}
    />
    <PlanGroup
      heading="Due, held for a later sweep"
      note={`Also due, but beyond the ${plan.maxPerTick} a single sweep queues. Nothing here is dropped; it is queued on a following sweep.`}
      rows={plan.deferred}
    />
  </div>
);

interface PlanGroupProps {
  readonly heading: string;
  readonly note: string;
  readonly rows: ScheduleDecisionView[];
}

const PlanGroup = ({ heading, note, rows }: PlanGroupProps) => {
  if (rows.length === 0) return null;
  return (
    <section className="sweep-group">
      <div className="sweep-group-head">
        <h3>{heading}</h3>
        <span className="sweep-count num">{rows.length}</span>
      </div>
      <p className="note sweep-group-note">{note}</p>
      <ul className="sweep-list">
        {rows.map((row) => (
          <li key={row.tenantId} className="sweep-row">
            <span className="sweep-name">{row.displayName}</span>
            <span className="note sweep-cadence num">
              {row.cadenceDays}-day cadence
              {row.schedule.cadenceDays === null && ' (deployment default)'}
            </span>
            {/* Verbatim: the scheduler writes this sentence, and the API, the CLI
                and this screen have to say the same thing about the same tenant. */}
            <p className="note sweep-detail">{row.detail}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};

interface ConfigPanelProps {
  readonly canManage: boolean;
}

const ConfigPanel = ({ canManage }: ConfigPanelProps) => {
  const [config, setConfig] = useState<SchedulerConfig | null>(null);
  const [cadence, setCadence] = useState('');
  const [maxPerTick, setMaxPerTick] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const apply = useCallback((next: SchedulerConfig) => {
    setConfig(next);
    setCadence(next.defaultCadenceDays === null ? '' : String(next.defaultCadenceDays));
    setMaxPerTick(next.maxPerTick === null ? '' : String(next.maxPerTick));
  }, []);

  useEffect(() => {
    let live = true;
    api
      .getSchedulerConfig()
      .then((next) => {
        if (!live) return;
        apply(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (live) setError(message(cause));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [apply]);

  // An empty input is a deliberate value here — "follow the built-in default" —
  // not a missing one, so it maps to null rather than being treated as unset.
  const parsed = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : Number.NaN;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    const defaultCadenceDays = parsed(cadence);
    const nextMax = parsed(maxPerTick);
    if (Number.isNaN(defaultCadenceDays) || Number.isNaN(nextMax)) {
      setError('A cadence and a per-tick cap are whole numbers, or empty to use the built-in default.');
      return;
    }

    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const saved = await api.putSchedulerConfig({ defaultCadenceDays, maxPerTick: nextMax });
      apply(saved);
      setStatus('Deployment schedule saved.');
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card sweep-card" aria-labelledby="sweep-config-heading">
      <div className="card-head">
        <h2 id="sweep-config-heading">Deployment schedule</h2>
      </div>
      <div className="card-body">
        <p className="note sweep-config-lead">
          The defaults every client follows unless it pins its own cadence. Leave a field empty to use
          the built-in default rather than a number you have chosen.
        </p>

        {error !== null && <div className="error sweep-error">{error}</div>}
        {status !== null && <p className="status-inline sweep-status">{status}</p>}

        {loading || config === null ? (
          <p className="note">Loading…</p>
        ) : (
          <form onSubmit={(event) => void submit(event)} noValidate>
            <div className="field sweep-field">
              <label htmlFor="sweep-cadence">Default cadence (days)</label>
              <input
                id="sweep-cadence"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                autoComplete="off"
                placeholder={String(config.defaults.defaultCadenceDays)}
                value={cadence}
                disabled={busy || !canManage}
                onChange={(event) => setCadence(event.target.value)}
              />
              <span className="hint">
                Empty follows the built-in default of {config.defaults.defaultCadenceDays} days.
              </span>
            </div>

            <div className="field sweep-field">
              <label htmlFor="sweep-max">Clients queued per sweep</label>
              <input
                id="sweep-max"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                autoComplete="off"
                placeholder={String(config.defaults.maxPerTick)}
                value={maxPerTick}
                disabled={busy || !canManage}
                onChange={(event) => setMaxPerTick(event.target.value)}
              />
              <span className="hint">
                Microsoft throttles per tenant, so a sweep queues at most this many at once; the rest
                wait for a following sweep. Empty follows the built-in default of{' '}
                {config.defaults.maxPerTick}.
              </span>
            </div>

            {canManage ? (
              <button type="submit" className="btn btn-primary" aria-busy={busy || undefined} disabled={busy}>
                {busy ? 'Saving…' : 'Save schedule'}
              </button>
            ) : (
              <p className="note">Changing the deployment schedule needs the deployment.manage capability.</p>
            )}

            {config.updatedAt === null && canManage && (
              <p className="note sweep-config-note">
                Never set — both fields are following the built-in defaults.
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
};
